import { api } from '../lib/api-client';
import { ok, readApiResult, type DataResult } from '../lib/data-result';
import { issueStatusBadge } from '../lib/status-ui';
import type { CreateIssueRequest, IssueListItem, PaginatedData } from '../types/api';

type IssueView = 'open' | 'closed';

type IssueListQuery = {
  page: number;
  limit: number;
  view: IssueView;
  sort: string;
  priority?: string | null;
  search?: string;
};

function safeText(value: string | number | null | undefined): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function priorityLabel(priority: string | null): string {
  if (priority === 'high') return 'high-priority';
  if (priority === 'medium') return 'medium-priority';
  if (priority === 'low') return 'low-priority';
  return '';
}

function priorityDisplay(priority: string | null | undefined): string {
  if (priority === 'high') return 'High';
  if (priority === 'medium') return 'Medium';
  if (priority === 'low') return 'Low';
  return 'Priority';
}

function priorityClass(priority: string | null | undefined): string {
  if (priority === 'high') return 'issue-pill-priority-high';
  if (priority === 'medium') return 'issue-pill-priority-medium';
  if (priority === 'low') return 'issue-pill-priority-low';
  return 'issue-pill-muted';
}

function activeFilterParts(input: { priority: string | null; search: string }): string[] {
  const parts: string[] = [];
  if (input.priority) parts.push(priorityLabel(input.priority));
  if (input.search) parts.push('matching "' + input.search.trim() + '"');
  return parts;
}

async function loadTabCounts(): Promise<DataResult<{ open: number | ''; closed: number | '' }>> {
  const [openRes, closedRes] = await Promise.all([
    readApiResult(() => api.issues.list({ view: 'open', limit: 1 }), 'Failed to load open issue count'),
    readApiResult(() => api.issues.list({ view: 'closed', limit: 1 }), 'Failed to load closed issue count'),
  ]);

  return ok({
    open: openRes.ok ? openRes.data.total : '',
    closed: closedRes.ok ? closedRes.data.total : '',
  });
}

async function loadIssues(query: IssueListQuery): Promise<DataResult<PaginatedData<IssueListItem>>> {
  return readApiResult(
    () => api.issues.list({
      page: query.page,
      limit: query.limit,
      view: query.view,
      sort: query.sort,
      ...(query.priority && { priority: query.priority }),
      ...(query.search && { q: query.search }),
    }),
    'Failed to load skill requests',
    { networkError: 'Failed to load skill requests' },
  );
}

async function createIssue(request: CreateIssueRequest): Promise<DataResult<{ id: number }>> {
  return readApiResult(
    () => api.issues.create(request),
    'Failed to create skill request',
    {
      map: (data) => ({ id: data.id }),
      networkError: 'Network error',
    },
  );
}

function filterSummaryText(input: {
  total: number;
  view: IssueView;
  priority: string | null;
  search: string;
}): string {
  const parts = activeFilterParts(input);
  if (!parts.length) return '';
  return 'Showing ' + input.total + ' ' + input.view + ' requests filtered by ' + parts.join(' and ');
}

function emptyMessage(input: { view: IssueView; priority: string | null; search: string }): string {
  const parts = activeFilterParts(input);
  if (!parts.length) return 'No skill requests found';
  return 'No ' + parts.join(' ') + ' ' + input.view + ' requests found';
}

function renderIssueSignals(issue: IssueListItem): string {
  const signals: string[] = [];
  const submissionCount = Number(issue.submission_count || 0);
  const latestSha = String(issue.latest_commit_sha || '').trim();
  const latestMessage = String(issue.latest_commit_message || '').trim();
  const skillId = Number(issue.skill_id || 0);
  const relatedSkillId = Number(issue.related_skill_id || 0);

  if (issue.request_origin === 'official') {
    signals.push(
      '<span class="issue-pill issue-pill-official">' +
        '<i class="fas fa-certificate"></i>Official request' +
      '</span>',
    );
  }

  signals.push(
    '<span class="issue-pill ' + priorityClass(issue.priority) + '">' +
      '<i class="fas fa-flag"></i>' + priorityDisplay(issue.priority) +
    '</span>',
  );

  signals.push(
      '<span class="issue-pill ' + (submissionCount > 0 ? 'issue-pill-submissions' : 'issue-pill-muted') + '">' +
      '<i class="fas fa-layer-group"></i>' +
      (submissionCount === 0 ? 'No submissions' : (submissionCount === 1 ? '1 submission' : submissionCount + ' submissions')) +
    '</span>',
  );

  if (skillId > 0) {
    signals.push(
      '<span class="issue-pill issue-pill-skill">' +
        '<i class="fas fa-puzzle-piece"></i>Published skill #' + skillId +
      '</span>',
    );
  } else if (relatedSkillId > 0) {
    signals.push(
      '<span class="issue-pill issue-pill-skill">' +
        '<i class="fas fa-puzzle-piece"></i>Related skill #' + relatedSkillId +
      '</span>',
    );
  }

  if (latestSha) {
    signals.push(
      '<span class="issue-pill issue-pill-commit">' +
        '<i class="fas fa-code-commit"></i>' + safeText(latestSha.slice(0, 8)) +
      '</span>' +
      (latestMessage ? '<span class="issue-commit-message">' + safeText(latestMessage) + '</span>' : ''),
    );
  }

  return signals.join('');
}

function renderIssues(items: readonly IssueListItem[]): string {
  return items.map((issue, index) => {
    const updatedTime = issue.updated_at || issue.created_at;
    const voteCount = issue.vote_count || 0;
    const submitterName = issue.submitter_name || '';
    return '<a href="/issues/' + issue.id + '" class="issue-row animate-in" style="animation-delay:' + (index * 35) + 'ms">' +
      '<div class="issue-row-main">' +
        '<div class="issue-row-titleline">' +
          '<span class="issue-id">#' + issue.id + '</span>' +
          '<h3 class="issue-title">' + safeText(issue.title) + '</h3>' +
          issueStatusBadge(issue.status, issue.closure_kind) +
        '</div>' +
        '<div class="issue-row-signals">' + renderIssueSignals(issue) + '</div>' +
      '</div>' +
      '<div class="issue-row-right">' +
        '<span class="issue-meta"><i class="fas fa-arrow-up"></i>' + voteCount + ' want this</span>' +
        (submitterName ? '<span class="issue-meta">' + safeText(submitterName) + '</span>' : '') +
        '<span class="issue-time">' + timeAgo(updatedTime) + '</span>' +
      '</div>' +
    '</a>';
  }).join('');
}

function renderPagination(data: PaginatedData<IssueListItem>): string {
  if (data.totalPages <= 1) return '';
  let html = '';
  for (let page = 1; page <= data.totalPages; page += 1) {
    html += '<button onclick="window.currentPage=' + page + ';window.loadIssues()" class="w-8 h-8 rounded-lg text-xs border ' +
      (page === data.page ? 'bg-brand-600 text-white border-brand-500/60' : 'bg-surface-2 text-zinc-400 border-zinc-700 hover:bg-surface-3 hover:text-white') + '">' + page + '</button>';
  }
  return html;
}

function renderError(message: string): string {
  return errorStateHtml(message);
}

window.IssuesPageData = {
  loadTabCounts,
  loadIssues,
  createIssue,
};

window.IssuesPageRenderers = {
  filterSummaryText,
  emptyMessage,
  renderIssues,
  renderPagination,
  renderError,
};
